import React, { useEffect, useState, useCallback } from 'react';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Sparkles, Check, ArrowRight, X, Target, Heart, Users, Loader2 } from 'lucide-react';
import { useAuth } from '../app/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '../app/api';
import { logger } from '../lib/logger';

/**
 * AICoachTrialPopup (Phase 25)
 *
 * Site-wide modal that invites visitors to start a 7-day free trial of the
 * full Apex AI Coach — no credit card, signup completes inside the modal.
 *
 * Truthful promise mechanics:
 *  - POST /api/auth/signup with `source: "ai_coach_trial_popup"` triggers a
 *    server-side `trial_grant_until = now + 7d` on the user record.
 *  - `_user_tier()` honors that grant and returns "pro" until it expires.
 *  - No Stripe round-trip. No card collected. Reverts to free after 7 days.
 *
 * Frequency rules:
 *  - Logged-in users: never see it.
 *  - Once per session (sessionStorage) — even if they navigate away.
 *  - Hard dismiss with "Maybe later": 7-day cooldown (localStorage).
 *  - Submitted successfully: permanent (localStorage) — they have an account.
 *  - Excluded routes (mid-conversion / already in-app):
 *      /app/*, /admin/*, /assessment, /book, /auth/*, /locker-room/welcome
 *  - Engagement-based fire: whichever wins first —
 *      (a) visitor scrolls past 40% of the page (engaged reader), OR
 *      (b) 25s elapses (slow scroller / quick bouncer safety net).
 *    This avoids the "instant ambush" feel and only catches visitors who
 *    have started to invest attention in the page.
 */

const SESSION_KEY = 'apex.aiCoachTrialPopup.shown.v1';
const DISMISS_UNTIL_KEY = 'apex.aiCoachTrialPopup.dismissUntil.v1';
const COMPLETED_KEY = 'apex.aiCoachTrialPopup.completed.v1';
// Engagement-based fire: whichever happens first wins.
//   - Scroll past SCROLL_THRESHOLD_PCT of the page  (engaged-reader signal)
//   - OR FIRE_DELAY_MS elapses                      (catch slow scrollers / quick bouncers)
const FIRE_DELAY_MS = 25000;
const SCROLL_THRESHOLD_PCT = 0.40;
const DISMISS_COOLDOWN_DAYS = 7;

const EXCLUDED_PREFIXES = [
  '/app',
  '/admin',
  '/assessment',
  '/book',
  '/auth',
  '/locker-room/welcome',
];

function shouldExcludePath(pathname) {
  if (!pathname) return false;
  return EXCLUDED_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

function readDismissUntil() {
  try {
    const raw = window.localStorage.getItem(DISMISS_UNTIL_KEY);
    if (!raw) return 0;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) ? n : 0;
  } catch (e) {
    // Storage blocked — treat as "no cooldown set" so the popup logic
    // continues to evaluate the other gates.
    logger.debug('AICoachTrialPopup dismiss-until read blocked', e);
    return 0;
  }
}

const AUDIENCES = [
  { id: 'athlete', icon: Target, label: 'Athlete', tagline: 'Compete with confidence.' },
  { id: 'parent',  icon: Heart,  label: 'Parent',  tagline: 'Support without pressure.' },
  { id: 'coach',   icon: Users,  label: 'Coach',   tagline: 'Build a tougher program.' },
];

export const AICoachTrialPopup = () => {
  const { user, loading: authLoading, refresh } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [audience, setAudience] = useState('athlete');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Trigger logic: fire the popup once per session on a non-excluded route.
  // Whichever signals engagement first wins:
  //   - user scrolls past SCROLL_THRESHOLD_PCT of the document
  //   - OR FIRE_DELAY_MS elapses
  useEffect(() => {
    if (authLoading) return;
    if (user) return;                                 // logged-in users: skip
    if (shouldExcludePath(location.pathname)) return; // mid-conversion routes: skip
    try {
      if (window.localStorage.getItem(COMPLETED_KEY) === '1') return;
      if (window.sessionStorage.getItem(SESSION_KEY) === '1') return;
      if (readDismissUntil() > Date.now()) return;
    } catch (e) {
      // Storage blocked (private mode / quota exceeded). Falling through and
      // showing the popup is the correct behavior — log for dev visibility.
      logger.debug('AICoachTrialPopup storage read blocked', e);
    }

    let fired = false;
    const fire = () => {
      if (fired) return;
      fired = true;
      setOpen(true);
      try {
        window.sessionStorage.setItem(SESSION_KEY, '1');
      } catch (e) {
        logger.debug('AICoachTrialPopup session marker write failed', e);
      }
    };

    // (1) Time-based fallback — catches slow scrollers / readers who pause.
    const t = setTimeout(fire, FIRE_DELAY_MS);

    // (2) Scroll-based trigger — fires when the visitor has clearly engaged.
    //     Uses scrollY relative to scrollable height; guards against pages
    //     too short to ever cross the threshold by treating them as eligible
    //     once the bottom is in view.
    const onScroll = () => {
      const doc = document.documentElement;
      const scrollTop = window.scrollY || doc.scrollTop || 0;
      const viewport = window.innerHeight || doc.clientHeight || 0;
      const fullHeight = doc.scrollHeight || 0;
      const scrollable = Math.max(0, fullHeight - viewport);
      if (scrollable <= 0) return; // page fits in viewport — let the timer handle it
      const pct = scrollTop / scrollable;
      if (pct >= SCROLL_THRESHOLD_PCT) fire();
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      clearTimeout(t);
      window.removeEventListener('scroll', onScroll);
    };
  }, [user, authLoading, location.pathname]);

  const dismiss = useCallback((mode) => {
    setOpen(false);
    if (mode === 'cooldown') {
      try {
        const until = Date.now() + DISMISS_COOLDOWN_DAYS * 24 * 60 * 60 * 1000;
        window.localStorage.setItem(DISMISS_UNTIL_KEY, String(until));
      } catch (e) {
        logger.debug('AICoachTrialPopup cooldown write failed', e);
      }
    }
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setError(null);
    const cleanName = name.trim();
    const cleanEmail = email.trim().toLowerCase();
    if (!cleanName) { setError('Please enter your name.'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) { setError('That email doesn’t look right.'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    setSubmitting(true);
    try {
      await api.post('/api/auth/signup', {
        name: cleanName,
        email: cleanEmail,
        password,
        audience,
        source: 'ai_coach_trial_popup',
        ref: `aic_${audience}`,
      });
      try {
        window.localStorage.setItem(COMPLETED_KEY, '1');
        window.localStorage.removeItem(DISMISS_UNTIL_KEY);
      } catch (e) {
        logger.debug('AICoachTrialPopup completion write failed', e);
      }
      toast.success(`Welcome, ${cleanName.split(' ')[0]}! Your 7-day trial is live.`);
      if (typeof refresh === 'function') {
        try { await refresh(); } catch (e) {
          // Non-fatal — the user is signed in via cookie, refresh just
          // re-hydrates client state.
          logger.warn('AICoachTrialPopup post-signup refresh failed', e);
        }
      }
      setOpen(false);
      navigate('/app/dashboard');
    } catch (err) {
      logger.warn('AI coach popup signup failed', err);
      const msg = err?.message || 'Could not create account';
      // Friendlier message for the common "email already exists" case
      if (/exist|already|registered/i.test(msg)) {
        setError("You already have an account — log in to continue.");
      } else {
        setError(msg);
      }
    } finally {
      setSubmitting(false);
    }
  }, [name, email, password, audience, refresh, navigate]);

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) dismiss('session'); }}>
      <DialogContent
        className="max-w-[640px] p-0 overflow-hidden bg-[#0B0C0D] text-white border-0 shadow-2xl gap-0 [&>button[aria-label='Close']]:hidden"
        data-testid="ai-coach-trial-popup"
      >
        {/* Header / pitch */}
        <div className="relative px-6 sm:px-8 pt-7 pb-6 bg-gradient-to-br from-[#0a0d0e] via-[#0f1416] to-[#0a0d0e]">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 right-0 w-1/2 opacity-[0.07]"
            style={{ backgroundImage: 'repeating-linear-gradient(135deg, rgba(255,255,255,0.5) 0 1px, transparent 1px 22px)' }}
          />
          <button
            type="button"
            onClick={() => dismiss('cooldown')}
            className="absolute top-3 right-3 inline-flex h-8 w-8 items-center justify-center rounded-full text-white/45 hover:text-white hover:bg-white/10 transition-colors"
            aria-label="Close"
            data-testid="ai-coach-trial-popup-close"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="relative">
            <div className="font-mono-accent uppercase tracking-[0.22em] text-[10px] text-[#4FBCC7] font-bold mb-3 inline-flex items-center gap-1.5">
              <Sparkles className="h-3 w-3" />
              Free · 7 Days · No Card Required
            </div>
            <h2
              className="font-heading text-[1.75rem] sm:text-[2.1rem] font-bold tracking-tight leading-[1.08] mb-3"
              data-testid="ai-coach-trial-popup-headline"
            >
              Train your mind.<br />
              <span className="text-[#4FBCC7]">Free for a week.</span>
            </h2>
            <p className="text-[14.5px] text-white/70 leading-[1.55] max-w-[52ch]">
              Apex AI Coach gives <strong className="text-white">athletes, parents, and coaches</strong> private, in-pocket access to Coach Lewis&rsquo;s mental performance method. Full access for 7 days. No credit card. After that, it reverts to the free mode &mdash; <em>never</em> a surprise charge.
            </p>
          </div>
        </div>

        {/* Body / form */}
        <form onSubmit={handleSubmit} className="px-6 sm:px-8 pt-6 pb-7 space-y-4">
          <div>
            <Label className="text-white/70 text-[11.5px] font-mono-accent uppercase tracking-[0.18em] font-semibold mb-2 block">
              I&rsquo;m signing up as a…
            </Label>
            <div className="grid grid-cols-3 gap-2" data-testid="ai-coach-trial-popup-audience">
              {AUDIENCES.map((a) => {
                const Icon = a.icon;
                const selected = audience === a.id;
                return (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => setAudience(a.id)}
                    data-testid={`ai-coach-trial-popup-audience-${a.id}`}
                    aria-pressed={selected}
                    className={`group rounded-xl p-3 text-left transition-all ring-1 ${
                      selected
                        ? 'bg-[#004C54]/35 ring-[#4FBCC7] shadow-md'
                        : 'bg-white/[0.04] ring-white/10 hover:bg-white/[0.08] hover:ring-white/20'
                    }`}
                  >
                    <Icon className={`h-4 w-4 mb-1.5 ${selected ? 'text-[#4FBCC7]' : 'text-white/55'}`} />
                    <div className="font-heading text-[13.5px] font-bold leading-tight">{a.label}</div>
                    <div className="text-[10.5px] text-white/45 mt-0.5 leading-tight">{a.tagline}</div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="aic-name" className="text-white/70 text-[11.5px] font-mono-accent uppercase tracking-[0.18em] font-semibold">
                Name
              </Label>
              <Input
                id="aic-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                autoComplete="name"
                className="mt-1.5 bg-white/[0.06] border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[#4FBCC7]"
                data-testid="ai-coach-trial-popup-name"
              />
            </div>
            <div>
              <Label htmlFor="aic-email" className="text-white/70 text-[11.5px] font-mono-accent uppercase tracking-[0.18em] font-semibold">
                Email
              </Label>
              <Input
                id="aic-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                className="mt-1.5 bg-white/[0.06] border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[#4FBCC7]"
                data-testid="ai-coach-trial-popup-email"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="aic-password" className="text-white/70 text-[11.5px] font-mono-accent uppercase tracking-[0.18em] font-semibold">
              Password
            </Label>
            <Input
              id="aic-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="8+ characters"
              autoComplete="new-password"
              className="mt-1.5 bg-white/[0.06] border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[#4FBCC7]"
              data-testid="ai-coach-trial-popup-password"
            />
          </div>

          {error && (
            <div
              className="text-[13px] text-red-300 bg-red-500/10 ring-1 ring-red-400/30 rounded-lg px-3 py-2"
              data-testid="ai-coach-trial-popup-error"
            >
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={submitting}
            className="w-full h-12 text-[14.5px] font-bold rounded-xl bg-[#004C54] hover:bg-[#003a40] text-white shadow-lg shadow-[#004C54]/30 active:scale-[0.98]"
            data-testid="ai-coach-trial-popup-submit"
          >
            {submitting ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Creating your account…</>
            ) : (
              <>Create my free account <ArrowRight className="h-4 w-4 ml-2" /></>
            )}
          </Button>

          {/* Trust strip */}
          <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 text-[11.5px] text-white/55 pt-1">
            <span className="inline-flex items-center gap-1"><Check className="h-3 w-3 text-[#4FBCC7]" strokeWidth={2.5} /> 7 days free</span>
            <span className="inline-flex items-center gap-1"><Check className="h-3 w-3 text-[#4FBCC7]" strokeWidth={2.5} /> No credit card</span>
            <span className="inline-flex items-center gap-1"><Check className="h-3 w-3 text-[#4FBCC7]" strokeWidth={2.5} /> Reverts to free, never charged</span>
          </div>

          <div className="flex items-center justify-between pt-2 text-[12.5px]">
            <a href="/app/login" className="text-white/55 hover:text-white transition-colors" data-testid="ai-coach-trial-popup-login-link">
              Already have an account? <span className="underline underline-offset-2">Sign in</span>
            </a>
            <button
              type="button"
              onClick={() => dismiss('cooldown')}
              className="text-white/45 hover:text-white/80 transition-colors"
              data-testid="ai-coach-trial-popup-maybe-later"
            >
              Maybe later
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AICoachTrialPopup;
